/**
 * The one HTTP client. Every request carries the bearer token, every non-2xx
 * becomes an ApiError with the backend's problem+json title, and a 401 clears
 * the stored session so the gate shows sign-in instead of a page of errors.
 */
import type {
  AuditRow,
  CertificateResponse,
  EquipmentRow,
  GenesisImportResult,
  GenesisPreview,
  ListSessionsResponse,
  OverviewStats,
  ProcedureRow,
  SessionDetailResponse,
  StepRow,
  UserRole,
  UserRow,
} from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface SessionListQuery {
  status?: string;
  limit?: number;
  since?: string;
}

export class ApiClient {
  constructor(
    readonly apiHost: string,
    private readonly tokenProvider: () => string | undefined,
    private readonly onUnauthorized: () => void = () => undefined,
  ) {}

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = this.tokenProvider();
    let res: Response;
    try {
      res = await fetch(`${this.apiHost}${path}`, {
        method,
        headers: {
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new ApiError(0, 'Could not reach the server. Check your connection and try again.');
    }
    if (res.status === 401) this.onUnauthorized();
    if (!res.ok) {
      const problem = (await res.json().catch(() => ({}))) as { title?: string; code?: string };
      throw new ApiError(res.status, problem.title ?? friendlyStatus(res.status), problem.code);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // --- sessions ---
  listSessions(query: SessionListQuery = {}): Promise<ListSessionsResponse> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.limit) params.set('limit', String(query.limit));
    if (query.since) params.set('since', query.since);
    const qs = params.toString();
    return this.request<ListSessionsResponse>('GET', `/api/sessions${qs ? `?${qs}` : ''}`);
  }
  getSession(id: string): Promise<SessionDetailResponse> {
    return this.request<SessionDetailResponse>('GET', `/api/sessions/${id}`);
  }
  getSessionAudit(id: string): Promise<{ auditLog: AuditRow[] }> {
    return this.request<{ auditLog: AuditRow[] }>('GET', `/api/sessions/${id}/audit`);
  }
  postNote(id: string, text: string, stepNumber?: number): Promise<{ auditId: string }> {
    return this.request<{ auditId: string }>('POST', `/api/sessions/${id}/notes`, {
      text,
      stepNumber,
    });
  }
  requestReport(id: string, format: 'letter' | 'a4' = 'letter'): Promise<{ queued: boolean }> {
    return this.request<{ queued: boolean }>('POST', `/api/sessions/${id}/report`, { format });
  }
  getCertificate(id: string): Promise<CertificateResponse> {
    return this.request<CertificateResponse>('GET', `/api/sessions/${id}/certificate`);
  }
  overrideVerdict(
    id: string,
    body: { priorAuditId: string; stepNumber: number; verified: boolean; message?: string },
  ): Promise<{ auditId: string }> {
    return this.request<{ auditId: string }>('POST', `/api/sessions/${id}/override`, body);
  }

  // --- statistics ---
  getOverviewStats(days: number): Promise<OverviewStats> {
    return this.request<OverviewStats>('GET', `/api/stats/overview?days=${days}`);
  }

  // --- admin: users ---
  listUsers(): Promise<{ users: UserRow[] }> {
    return this.request<{ users: UserRow[] }>('GET', '/api/admin/users');
  }
  createUser(body: { email: string; fullName: string; role: UserRole }): Promise<UserRow> {
    return this.request<UserRow>('POST', '/api/admin/users', body);
  }
  updateUser(
    id: string,
    body: { fullName?: string; role?: UserRole; isActive?: boolean },
  ): Promise<UserRow> {
    return this.request<UserRow>('PATCH', `/api/admin/users/${id}`, body);
  }

  // --- content (any signed-in role) ---
  resolveEquipment(
    qrValue: string,
  ): Promise<{ equipment: EquipmentRow; activeProcedure: ProcedureRow | null }> {
    return this.request('POST', '/api/equipment/resolve', { qrValue });
  }
  listProcedureSteps(procedureId: string): Promise<{ steps: StepRow[] }> {
    return this.request('GET', `/api/procedures/${procedureId}/steps`);
  }

  // --- admin: content ---
  listEquipment(): Promise<EquipmentRow[]> {
    return this.request<EquipmentRow[]>('GET', '/api/admin/equipment');
  }
  createEquipment(body: Partial<EquipmentRow>): Promise<EquipmentRow> {
    return this.request<EquipmentRow>('POST', '/api/admin/equipment', body);
  }
  deleteEquipment(id: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>('DELETE', `/api/admin/equipment/${id}`);
  }
  genesisPreview(projectId: string): Promise<GenesisPreview> {
    return this.request<GenesisPreview>(
      'GET',
      `/api/admin/genesis/preview?projectId=${encodeURIComponent(projectId)}`,
    );
  }
  genesisImport(body: { projectId: string; refresh?: boolean }): Promise<GenesisImportResult> {
    return this.request<GenesisImportResult>('POST', '/api/admin/genesis/import', {
      ...body,
      materialize: true,
    });
  }
  createProcedure(body: Partial<ProcedureRow>): Promise<ProcedureRow> {
    return this.request<ProcedureRow>('POST', '/api/admin/procedures', body);
  }
  updateStep(id: string, body: Partial<StepRow>): Promise<StepRow> {
    return this.request<StepRow>('PUT', `/api/admin/steps/${id}`, body);
  }
  testPrompt(
    stepId: string,
    body: { prompt: string; photoBase64?: string },
  ): Promise<{
    stepId: string;
    mode: 'mock' | 'live';
    result: { verified: boolean; confidence: string; message: string; detail: string };
  }> {
    return this.request('POST', `/api/admin/steps/${stepId}/test-prompt`, body);
  }
}

function friendlyStatus(status: number): string {
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'Not found.';
  if (status >= 500) return 'The server had a problem. Try again in a moment.';
  return `Request failed (${status})`;
}
